import { createAsyncThunk } from "@reduxjs/toolkit"
import axios, { AxiosError, AxiosRequestConfig, AxiosResponse } from "axios"

export interface FileState {
	id: number
	fileName: string
	originalFileName: string
	idBank: string
	corporateSenderId: string
	totalAmount: number
	plicksCount: number
	status: string
	location: null | string
	approved: boolean
	correctFile: boolean
	loadMassiveId: null | number
	creationDate: string
	sftpDate: null | string
	flagFirstReminder: boolean
	flagSecondReminder: boolean
	campaignIdLogo: string
	lastAuthorizedBy: undefined | string
	createdBy: string
	authorizationType: string
	campaignName: string
	finalized: boolean
	fileVersion: number
	checkIban: boolean
}

export interface SortElement {
	sorted: boolean
	unsorted: boolean
	empty: boolean
}

export interface PageableElement {
	content: FileState[]
	pageable: {
		sort: SortElement
		offset: number
		pageNumber: number
		pageSize: number
		paged: boolean
		unpaged: boolean
	}
}

export interface PageElement {
	content: FileState[]
	pageable: PageableElement
	totalElements: number
	totalPages: number
	last: boolean
	size: number
	number: number
	first: boolean
	numberOfElements: number
	empty: boolean
}

export interface FilesListResponse {
	nextUrl: null | string
	page: PageElement
	filter: unknow | undefined
	sort: unknow | undefined
}

export interface FilesListState {
	loading: boolean
	error: undefined | AxiosError<string>
	loadingFetch: boolean
	errorFetch: undefined | AxiosError<string>
	list: FilesListResponse
}

export interface FilePayload {
	filter?: unknown | undefined
	sort?: unknown | undefined
	page?: number | undefined
	pageSize?: number
}

export interface FileNamePayload {
	bankId: string
	corporateId?: string
	campaignLogo?: string
}

interface DownloadPayload {
	bankId: string
	filename: string
	fileId: string
}

export interface FetchFilePayload {
	apiName: string
	url: string
}

let abortController: AbortController | null = null

export const getDownload = createAsyncThunk<
	boolean,
	DownloadPayload,
	{ rejectValue: string }
>(
	"files/getDownload",
	async (
		{ bankId, fileId, filename }: DownloadPayload,
		{ rejectWithValue }
	) => {
		try {
			const config: AxiosRequestConfig = {
				responseType: "blob",
				withCredentials: true,
			}

			const axiosUrl = `${process.env.REACT_APP_FILE_SERVICE_API_URL}/fileservice/apifile/v2/${bankId}/${fileId}/download`
			const response: AxiosResponse<Blob> = await axios.get(axiosUrl, config)

			const contentDisposition = response.headers["content-disposition"]
			let filenameDownload = filename // fallback se non trovato

			if (contentDisposition) {
				const match = contentDisposition.match(/filename="?([^"]+)"?/)
				if (match?.[1]) {
					filenameDownload = match[1]
				}
			}

			// Crea il link per il download
			const blob = new Blob([response.data])
			const link = document.createElement("a")
			link.href = window.URL.createObjectURL(blob)
			link.download = filenameDownload
			document.body.appendChild(link)
			link.click()
			document.body.removeChild(link)

			return true
		} catch (error) {
			const axiosError = error as AxiosError<any>
			return rejectWithValue(
				axiosError?.response?.data ?? { message: "error.generic" }
			)
		}
	}
)

export const getFiles = createAsyncThunk<
	FilesListResponse,
	FilePayload,
	{ rejectValue: string }
>(
	"files/getFiles",
	async (
		{ page, pageSize, filter, sort }: FilePayload,
		{ rejectWithValue, dispatch }
	) => {
		try {
			if (abortController) {
				abortController.abort()
			}

			abortController = new AbortController()

			const config: AxiosRequestConfig = {
				headers: {
					"Content-Type": "application/json",
				},
				withCredentials: true,
			}

			const baseUrl = `${process.env.REACT_APP_FILE_SERVICE_API_URL}/fileservice/apifile/v2/files`
			const parameters = new URLSearchParams()

			if (page === 0 || page) {
				parameters.append("page", page.toString())
			}
			if (pageSize) {
				parameters.append("size", pageSize.toString())
			}
			if (filter && Object.keys(filter).length > 0) {
				parameters.append("filter", JSON.stringify(filter))
			}
			if (sort) {
				parameters.append("sort", sort)
			}
			const axiosUrl = `${baseUrl}${parameters ? "?" + parameters.toString() : ""}`
			const response: AxiosResponse<FilesListResponse> = await axios.get(
				axiosUrl,
				config
			)
			response.data.filter = filter
			response.data.sort = sort
			return response.data
		} catch (error) {
			const axiosError = error as AxiosError<FilesListResponse>
			return rejectWithValue(
				axiosError?.response?.data ?? { message: "error.generic" }
			)
		}
	}
)

export const getFilenames = createAsyncThunk<
	string[],
	FileNamePayload,
	{ rejectValue: string }
>(
	"files/getFilenames",
	async (
		{ bankId, corporateId, campaignLogo }: FileNamePayload,
		{ rejectWithValue }
	) => {
		try {
			const config: AxiosRequestConfig = {
				headers: {
					"Content-Type": "application/json",
				},
				withCredentials: true,
			}

			const baseUrl = `${process.env.REACT_APP_FILE_SERVICE_API_URL}/fileservice/apifile/${bankId}/filenames`
			const parameters = new URLSearchParams()

			if (corporateId) {
				parameters.append("corporateId", corporateId)
			}
			if (campaignLogo) {
				parameters.append("campaign", campaignLogo)
			}

			const axiosUrl = `${baseUrl}${parameters ? "?" + parameters.toString() : ""}`
			const response: AxiosResponse<FilesListResponse> = await axios.get(
				axiosUrl,
				config
			)
			return response.data
		} catch (error) {
			const axiosError = error as AxiosError<string>
			return rejectWithValue(axiosError?.response?.data ?? "error.generic")
		}
	}
)

export const fetchFiles = createAsyncThunk<
	FilesListResponse,
	FetchFilePayload,
	{ rejectValue: string }
>(
	"files/fetchFiles",
	async ({ url }: FetchFilePayload, { dispatch, rejectWithValue }) => {
		try {
			// Annulla la richiesta precedente se ancora in corso
			if (abortController) {
				abortController.abort()
			}

			// Crea nuovo controller
			abortController = new AbortController()

			const config: AxiosRequestConfig = {
				headers: {
					"Content-Type": "application/json",
				},
				withCredentials: true,
			}
			const response: AxiosResponse<FilesListResponse> = await axios.get(
				url,
				config
			)
			return response.data
		} catch (error) {
			const axiosError = error as AxiosError
			console.error("Errore Axios:", axiosError)

			if (axiosError.response) {
				return rejectWithValue(
					axiosError.response.data?.message || "error.generic"
				)
			} else if (axiosError.request) {
				return rejectWithValue("error.noResponseFromServer")
			} else {
				return rejectWithValue("error.unknown")
			}
		}
	}
)
